import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API, { fmt, PRIORITY } from '../utils/api';

const EMPTY = { title: '', description: '', customer_id: '', assigned_to: '', due_date: '', is_recurring: false, recurring_type: '', priority: 'normal' };
const PRIORITY_BADGE = { low: 'badge-gray', normal: 'badge-info', high: 'badge-danger' };

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [fStatus, setFStatus] = useState('pending');
  const [error, setError] = useState('');
  const isManager = ['admin', 'sales_manager'].includes(user?.role);

  const load = () => {
    API.get('/tasks').then(r => setTasks(r.data));
    API.get('/customers').then(r => setCustomers(r.data));
    if (isManager) API.get('/users').then(r => setUsers(r.data.filter(u => u.active)));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title) return setError('عنوان المهمة مطلوب');
    try {
      await API.post('/tasks', form);
      setModal(false); load();
    } catch (e) { setError(e.response?.data?.error || 'حدث خطأ'); }
  };

  const updateStatus = async (id, status) => {
    await API.put(`/tasks/${id}/status`, { status });
    load();
  };

  const filtered = tasks.filter(t => !fStatus || t.status === fStatus);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">📋 المهام</h1>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY); setError(''); setModal(true); }}>+ مهمة جديدة</button>
      </div>

      <div className="tabs mb-2">
        {[['pending', '⏳ معلقة'], ['done', '✅ منجزة'], ['', '📋 الكل']].map(([v, l]) => (
          <button key={v} className={`tab ${fStatus === v ? 'active' : ''}`} onClick={() => setFStatus(v)}>{l}</button>
        ))}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">📋 مهمة جديدة</span>
              <button className="modal-close" onClick={() => setModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div className="form-grid">
                <div className="form-group span-2"><label>العنوان <span className="required">*</span></label><input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
                <div className="form-group span-2"><label>الوصف</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} /></div>
                <div className="form-group"><label>العميل</label>
                  <select value={form.customer_id} onChange={e => setForm({ ...form, customer_id: e.target.value })}>
                    <option value="">-- بدون --</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {isManager && (
                  <div className="form-group"><label>تكليف إلى</label>
                    <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                      <option value="">-- نفسي --</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group"><label>تاريخ الاستحقاق</label><input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
                <div className="form-group"><label>الأولوية</label>
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.is_recurring} onChange={e => setForm({ ...form, is_recurring: e.target.checked })} style={{ width: 'auto' }} />
                    مهمة دورية؟
                  </label>
                </div>
                {form.is_recurring && (
                  <div className="form-group"><label>التكرار</label>
                    <select value={form.recurring_type} onChange={e => setForm({ ...form, recurring_type: e.target.value })}>
                      <option value="daily">يومي</option>
                      <option value="weekly">أسبوعي</option>
                      <option value="monthly">شهري</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={save}>💾 حفظ</button>
              <button className="btn btn-secondary" onClick={() => setModal(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <div className="table-scroll">
          <table>
            <thead><tr><th>العنوان</th><th>العميل</th><th>مكلف به</th><th>الأولوية</th><th>الاستحقاق</th><th>دوري؟</th><th>الحالة</th><th>إجراءات</th></tr></thead>
            <tbody>
              {filtered.map(t => {
                const isOverdue = t.due_date && new Date(t.due_date) < new Date() && t.status === 'pending';
                return (
                  <tr key={t.id}>
                    <td className="font-bold">{t.title}</td>
                    <td className="text-muted">{t.customer_name || '-'}</td>
                    <td className="text-muted">{t.assigned_name || '-'}</td>
                    <td><span className={`badge ${PRIORITY_BADGE[t.priority]}`}>{PRIORITY[t.priority]}</span></td>
                    <td className={isOverdue ? 'text-danger font-bold' : 'text-muted'}>{fmt.date(t.due_date)}{isOverdue ? ' ⚠️' : ''}</td>
                    <td>{t.is_recurring ? <span className="badge badge-warning">🔄 {t.recurring_type === 'daily' ? 'يومي' : t.recurring_type === 'weekly' ? 'أسبوعي' : 'شهري'}</span> : '-'}</td>
                    <td><span className={`badge ${t.status === 'pending' ? 'badge-warning' : t.status === 'done' ? 'badge-success' : 'badge-danger'}`}>{t.status === 'pending' ? 'معلقة' : t.status === 'done' ? 'منجزة' : 'ملغاة'}</span></td>
                    <td>
                      <div className="td-actions">
                        {t.status === 'pending' && <button className="btn btn-sm btn-success" onClick={() => updateStatus(t.id, 'done')}>✓ إنجاز</button>}
                        {isManager && t.status === 'pending' && <button className="btn btn-sm btn-danger" onClick={() => updateStatus(t.id, 'cancelled')}>إلغاء</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={8}><div className="empty-state"><div className="empty-state-icon">📋</div><p>لا توجد مهام</p></div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
